import { Controller } from "@hotwired/stimulus"
import { hideIntercom, showIntercom } from "../helpers/intercom"
import { confirmAction } from "../lib/confirm"

const SVG_PENCIL_SQUARE = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m16.862 4.487 1.687-1.688a1.875 1.875 0 1 1 2.652 2.652L10.582 16.07a4.5 4.5 0 0 1-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 0 1 1.13-1.897l8.932-8.931Zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0 1 15.75 21H5.25A2.25 2.25 0 0 1 3 18.75V8.25A2.25 2.25 0 0 1 5.25 6H10"/></svg>'
const SVG_CHECK = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m4.5 12.75 6 6 9-13.5"/></svg>'
const SVG_INFO_CIRCLE = '<svg class="w-3.5 h-3.5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor"><path stroke-linecap="round" stroke-linejoin="round" d="m11.25 11.25.041-.02a.75.75 0 0 1 1.063.852l-.708 2.836a.75.75 0 0 0 1.063.853l.041-.021M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9-3.75h.008v.008H12V8.25Z"/></svg>'

// Pay request backup files: PDFs, plus photos — the PDF gives each photo a
// page of its own (PdfMerger.append_file; Larry Daleo, Intercom 2026-11-03).
const PR_UPLOAD_EXTENSIONS = ["pdf", "jpg", "jpeg", "png", "gif", "heic", "heif", "webp", "tiff", "bmp"]
const prUploadable = f => PR_UPLOAD_EXTENSIONS.includes((f.name.split(".").pop() || "").toLowerCase())
const prContentType = f => f.type || (f.name.toLowerCase().endsWith(".pdf") ? "application/pdf" : "application/octet-stream")

export default class extends Controller {
  static targets = [
    "newCprModal", "modalTitle",
    "cprFedId", "cprInvoiceDate", "cprInvoiceNumber", "cprPo", "cprProjectNumber", "cprVendorId",
    "cprLineItems", "cprFeeTotal", "cprFaTotal", "cprAcaTotal", "cprPrevTotal", "cprThisTotal", "cprCompletedTotal", "cprPercentTotal", "cprBalanceTotal",
    "cprOther", "cprPrinting", "cprReimb", "cprTaxesType", "cprTaxes", "cprTaxes2Type", "cprTaxes2",
    "deleteModal", "signModal", "unsignModal", "requestApprovalModal",
    "cprDropzone", "cprFileInput", "cprFileList", "cprPickPhotos", "photoPicker",
    "addPastCprBtn", "saveDraftBtn", "signRequestBtn",
    "pastCprConfirmModal", "pastCprSequenceLabel",
    "cprHistoryLog", "cprHistoryLogEntries",
    "modalRequestApprovalBtn", "modalSignedBadge", "modalSignedText", "modalSignedTooltip", "modalSignedCheck",
    "unsignBtn", "modalPendingLabel", "modalDeleteBtn", "modalEditPastBtn", "modalApproveBtn", "modalRejectBtn",
    "rejectCprModal", "rejectComment",
    "overallTotals", "summaryScheduled", "summaryFa", "summaryAca", "summaryCompleted", "summaryPercent", "summaryBalance",
    "prCards", "emptyState",
    "uploadSection", "uploadLabel",
    "faSumBanner", "faSumAmount",
    "miscPrevTotal", "miscThisTotal", "taxesPrevTotal", "taxesThisTotal",
    "generalInfoModal",
    "giBuildingType", "giStakeMission", "giProjectNumber", "giPoNumber", "giVendorId", "giFedId",
    "giStartDate", "giEndDate",
    "giConsultantCompanyId", "giConsultantAddress", "giConsultantCity", "giConsultantState", "giConsultantZip",
    "renumberWrap", "renumberSelect"
  ]

  static values = {
    projectId: Number,
    currency: String,
    currencyFormat: String,
    delimiter: String,
    separator: String,
    isOwner: Boolean,
    // PM Permissions: stakeholder granted owner-side pay-request approval
    // (approve/reject side only — no editing or deleting).
    canApprove: Boolean,
    isConsultant: Boolean,
    feeScheduleApproved: Boolean,
    fasSum: Number,
    requirePrAttachment: Boolean
  }

  connect() {
    this._csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const pendingFlash = sessionStorage.getItem("prFlashMessage")
    if (pendingFlash) {
      sessionStorage.removeItem("prFlashMessage")
      this._showToast(pendingFlash)
    }
    this._editSequence = null
    this._isPast = false
    this._pendingDeleteSequence = null
    this._pendingSignSequence = null
    this._lineItemsData = []
    this._attachmentIds = []
    this._pendingUploads = 0
    this._faTotals = {}
    this._previousCprTotals = {}
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false
  }

  // ── Currency helpers ──────────────────────────────────────────────

  _parseCurrency(str) {
    if (!str) return 0
    let cleaned = str.replace(this.currencyValue, "").trim()
    let neg = false
    if (cleaned.startsWith("(") && cleaned.endsWith(")")) {
      neg = true
      cleaned = cleaned.slice(1, -1)
    }
    if (this.currencyFormatValue === "european_currency_format") {
      cleaned = cleaned.replace(/\./g, "").replace(",", ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      cleaned = cleaned.replace(/'/g, "")
    } else {
      cleaned = cleaned.replace(/,/g, "")
    }
    const val = parseFloat(cleaned) || 0
    return neg ? -val : val
  }

  _formatCurrency(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `${this.currencyValue}(${formatted})`
    return `${this.currencyValue}${formatted}`
  }

  _formatAmount(num) {
    if (num === null || num === undefined || isNaN(num)) return ""
    const abs = Math.abs(num)
    let formatted
    if (this.currencyFormatValue === "european_currency_format") {
      formatted = abs.toFixed(2).replace(".", ",").replace(/\B(?=(\d{3})+(?!\d))/g, ".")
    } else if (this.currencyFormatValue === "swiss_currency_format") {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, "'")
    } else {
      formatted = abs.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")
    }
    if (num < 0) return `(${formatted})`
    return formatted
  }

  _escAttr(str) {
    return String(str).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;").replace(/>/g, "&gt;")
  }

  // ── New CPR ─────────────────────────────────────────────────────

  async newPaymentRequest() {
    if (!this.feeScheduleApprovedValue) {
      this._showToast("Fee schedule must be approved before creating a payment request.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = false
    this.modalTitleTarget.textContent = "New Payment Request"
    await this._loadLineItemsAndShowModal()
  }

  async newPaidPaymentRequest() {
    if (!this.feeScheduleApprovedValue) {
      this._showToast("Fee schedule must be approved before creating a payment request.", "alert")
      return
    }
    this._editSequence = null
    this._isPast = true
    this.modalTitleTarget.textContent = "Past Payment Request"
    await this._loadLineItemsAndShowModal()
  }

  async editPaymentRequest(event) {
    this._hideRenumberSelect()
    const sequence = event.currentTarget.dataset.sequence
    this._editSequence = sequence
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    this._isPast = card?.dataset.isPast === "true"
    this.modalTitleTarget.textContent = this._isPast ? `Past Payment Request #${sequence}` : `Payment Request #${sequence}`
    await this._loadLineItemsAndShowModal(sequence)
  }

  async _loadLineItemsAndShowModal(sequence = null) {
    const url = `/projects/${this.projectIdValue}/finances/new_cpr_modal_data${sequence ? `?sequence=${sequence}` : ""}`
    const resp = await fetch(url, { headers: { "Accept": "application/json" } })
    if (!resp.ok) {
      const err = await resp.json().catch(() => ({}))
      this._showToast(err.error || "An error occurred.", "alert")
      return
    }
    const data = await resp.json()

    this._lineItemsData = data.line_items
    this._faTotals = data.fa_totals || {}
    this._previousCprTotals = data.previous_cpr_totals || {}
    this._attachmentIds = []
    this._pendingUploads = 0

    // Populate invoice fields
    if (this.hasCprFedIdTarget) this.cprFedIdTarget.value = data.cpr_data?.fedid || data.fedid || ""
    if (this.hasCprInvoiceDateTarget) {
      const today = new Date().toISOString().slice(0, 10)
      const dateVal = data.cpr_data ? (data.cpr_data.invoice_date || "") : today
      const fp = this.cprInvoiceDateTarget._flatpickr
      if (fp) {
        fp.setDate(dateVal, true)
      } else {
        this.cprInvoiceDateTarget.value = dateVal
      }
    }
    if (this.hasCprInvoiceNumberTarget) this.cprInvoiceNumberTarget.value = data.cpr_data?.invoice_number || ""
    if (this.hasCprPoTarget) this.cprPoTarget.value = data.cpr_data?.po || data.po || ""
    if (this.hasCprVendorIdTarget) this.cprVendorIdTarget.value = data.vendor_id || ""
    if (this.hasCprProjectNumberTarget) this.cprProjectNumberTarget.value = data.project_number || ""

    // Populate misc fields
    this._clearCprFields()
    if (data.cpr_data?.misc_items) {
      data.cpr_data.misc_items.forEach(mi => {
        if (mi.type_of_misc === "other" && this.hasCprOtherTarget) this.cprOtherTarget.value = mi.amount ? this._formatAmount(mi.amount) : ""
        if (mi.type_of_misc === "printing" && this.hasCprPrintingTarget) this.cprPrintingTarget.value = mi.amount ? this._formatAmount(mi.amount) : ""
        if (mi.type_of_misc === "reimb" && this.hasCprReimbTarget) this.cprReimbTarget.value = mi.amount ? this._formatAmount(mi.amount) : ""
        if (mi.type_of_misc === "taxes" && this.hasCprTaxesTarget) this.cprTaxesTarget.value = mi.amount ? this._formatAmount(mi.amount) : ""
        if (mi.type_of_misc === "taxes_type" && this.hasCprTaxesTypeTarget) this.cprTaxesTypeTarget.value = mi.text || ""
        if (mi.type_of_misc === "taxes2" && this.hasCprTaxes2Target) this.cprTaxes2Target.value = mi.amount ? this._formatAmount(mi.amount) : ""
        if (mi.type_of_misc === "taxes2_type" && this.hasCprTaxes2TypeTarget) this.cprTaxes2TypeTarget.value = mi.text || ""
      })
    }

    // Clear file list
    if (this.hasCprFileListTarget) this.cprFileListTarget.innerHTML = ""

    this._renderCprLineItems(data.line_items, data.cpr_data?.items || [], this._faTotals, this._previousCprTotals)

    // Load existing attachments when editing
    if (data.attachments && data.attachments.length > 0) {
      data.attachments.forEach(att => {
        this._attachmentIds.push(att.id)
        const chip = this._createFileChip(att.filename, {
          removable: true,
          url: att.url,
          onRemove: () => {
            chip.remove()
            this._attachmentIds = this._attachmentIds.filter(id => id !== att.id)
          }
        })
        if (this.hasCprFileListTarget) this.cprFileListTarget.appendChild(chip)
      })
    }

    // Render history log
    this._renderHistoryLog(data.history_log || [])

    // Show/hide FA sum banner
    if (this.hasFaSumBannerTarget) {
      const fasSum = parseFloat(data.fas_sum) || 0
      if (fasSum !== 0) {
        this.faSumAmountTarget.textContent = `${this.currencyValue}${this._formatAmount(fasSum)}`
        this.faSumBannerTarget.classList.remove("hidden")
      } else {
        this.faSumBannerTarget.classList.add("hidden")
      }
    }

    // Render modal header badges
    this._renderModalHeaderBadges(data)

    // Show/hide Add Past CPR button
    if (this.hasAddPastCprBtnTarget) {
      this.addPastCprBtnTarget.classList.toggle("hidden", !!this._editSequence)
    }

    this.newCprModalTarget.classList.remove("hidden")
    hideIntercom()
  }

  _renderCprLineItems(lineItems, existingItems, faTotals, previousCprTotals) {
    const existing = {}
    existingItems.forEach(item => { existing[item.line_item_id] = item })
    const cur = this.currencyValue

    let html = ""
    lineItems.forEach(li => {
      const ex = existing[li.id]
      const fee = parseFloat(li.fee) || 0
      const faTotal = parseFloat(faTotals[li.id]) || 0
      const adjContract = fee + faTotal
      const prevCpr = parseFloat(previousCprTotals[li.id]) || 0
      const currentAmount = ex ? (parseFloat(ex.amount) || 0) : 0
      const completedToDate = prevCpr + currentAmount
      const pctCompleted = adjContract > 0 ? ((completedToDate / adjContract) * 100).toFixed(0) + "%" : ""
      const balance = Math.round((adjContract - completedToDate) * 100) / 100

      const C = (val, field, extra = "") => `<td class="px-0 py-0 border-r border-gray-200${extra}" ${field ? `data-field="${field}"` : ""}><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]">${cur}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${val}</span></div></td>`

      html += `
        <tr data-line-item-id="${li.id}">
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate">${li.item || ""}</td>
          <td class="px-2 py-1 border-r border-gray-200 text-[12px] truncate">${li.work || ""}</td>
          ${C(this._formatAmount(fee), "fee")}
          ${C(faTotal !== 0 ? this._formatAmount(faTotal) : "", "fa-total")}
          ${C(adjContract > 0 ? this._formatAmount(adjContract) : "", "adj-contract")}
          <td class="px-0 py-0 border-r border-gray-200" data-field="prev-cpr" data-prev-total="${prevCpr}"><div class="flex items-center"><span class="pl-2 text-gray-400 select-none text-[12px]">${cur}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px]">${prevCpr > 0 ? this._formatAmount(prevCpr) : ""}</span></div></td>
          <td class="px-0 py-0 border-r border-gray-200 bg-amber-50">
            <div class="flex items-center">
              <span class="pl-2 text-gray-400 select-none text-[12px]">${cur}</span>
              <input type="text" value="${ex ? this._formatAmount(currentAmount) : ""}" data-field="this_application"
                data-action="input->consultant-pay-request#recalcCprTotals"
                class="w-full px-1 py-1 pr-2 text-right text-[12px] bg-transparent focus:outline-none">
            </div>
          </td>
          ${C(completedToDate > 0 ? this._formatAmount(completedToDate) : "", "completed-to-date")}
          <td class="px-2 py-1 text-center border-r border-gray-200 text-[12px]" data-field="pct-completed">${pctCompleted}</td>
          <td class="px-0 py-0" data-field="balance"><div class="flex items-center"><span class="pl-2 ${balance < 0 ? "text-red-600" : "text-gray-400"} select-none text-[12px]" data-sym="balance">${cur}</span><span class="w-full px-1 py-1 pr-2 text-right text-[12px] ${balance < 0 ? "text-red-600" : ""}">${this._formatAmount(balance)}</span></div></td>
        </tr>`
    })
    this.cprLineItemsTarget.innerHTML = html
    this._recalcFooterTotals()
  }

  _clearCprFields() {
    if (this.hasCprOtherTarget) this.cprOtherTarget.value = ""
    if (this.hasCprPrintingTarget) this.cprPrintingTarget.value = ""
    if (this.hasCprReimbTarget) this.cprReimbTarget.value = ""
    if (this.hasCprTaxesTarget) this.cprTaxesTarget.value = ""
    if (this.hasCprTaxesTypeTarget) this.cprTaxesTypeTarget.value = ""
    if (this.hasCprTaxes2Target) this.cprTaxes2Target.value = ""
    if (this.hasCprTaxes2TypeTarget) this.cprTaxes2TypeTarget.value = ""
  }

  // ── Calculation ───────────────────────────────────────────────────

  recalcCprTotals() {
    // Update per-row computed columns (value is in the second span inside the flex div)
    this.cprLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const adjContract = this._parseCurrency(row.querySelector("[data-field='adj-contract']")?.textContent)
      const prevCpr = parseFloat(row.querySelector("[data-field='prev-cpr']")?.dataset.prevTotal) || 0
      const thisApp = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      const completedToDate = prevCpr + thisApp
      const pctCompleted = adjContract > 0 ? ((completedToDate / adjContract) * 100).toFixed(0) + "%" : ""
      const balance = Math.round((adjContract - completedToDate) * 100) / 100

      const ctdSpan = row.querySelector("[data-field='completed-to-date'] span:last-child")
      if (ctdSpan) ctdSpan.textContent = completedToDate > 0 ? this._formatAmount(completedToDate) : ""
      const pctEl = row.querySelector("[data-field='pct-completed']")
      if (pctEl) pctEl.textContent = pctCompleted
      const balSpan = row.querySelector("[data-field='balance'] span:last-child")
      if (balSpan) {
        balSpan.textContent = this._formatAmount(balance)
        balSpan.classList.toggle("text-red-600", balance < 0)
      }
      const balSym = row.querySelector("[data-sym='balance']")
      if (balSym) {
        balSym.textContent = this.currencyValue
        balSym.classList.toggle("text-red-600", balance < 0)
        balSym.classList.toggle("text-gray-400", balance >= 0)
      }
    })
    this._recalcFooterTotals()
  }

  _recalcFooterTotals() {
    const cur = this.currencyValue
    let feeTotal = 0, faTotal = 0, acaTotal = 0, prevTotal = 0, thisTotal = 0, completedTotal = 0, balanceTotal = 0

    this.cprLineItemsTarget.querySelectorAll("tr").forEach(row => {
      feeTotal += this._parseCurrency(row.querySelector("[data-field='fee']")?.textContent)
      faTotal += this._parseCurrency(row.querySelector("[data-field='fa-total']")?.textContent)
      acaTotal += this._parseCurrency(row.querySelector("[data-field='adj-contract']")?.textContent)
      prevTotal += parseFloat(row.querySelector("[data-field='prev-cpr']")?.dataset.prevTotal) || 0
      thisTotal += this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      completedTotal += this._parseCurrency(row.querySelector("[data-field='completed-to-date']")?.textContent)
      balanceTotal += this._parseCurrency(row.querySelector("[data-field='balance']")?.textContent)
    })

    const pctTotal = acaTotal > 0 ? ((completedTotal / acaTotal) * 100).toFixed(1) + "%" : "0.0%"

    const setFooter = (target, hasTarget, val) => { if (hasTarget) { const s = target.querySelector("span:last-child"); if (s) s.textContent = val } }
    setFooter(this.cprFeeTotalTarget, this.hasCprFeeTotalTarget, this._formatAmount(feeTotal))
    setFooter(this.cprFaTotalTarget, this.hasCprFaTotalTarget, this._formatAmount(faTotal))
    setFooter(this.cprAcaTotalTarget, this.hasCprAcaTotalTarget, this._formatAmount(acaTotal))
    setFooter(this.cprPrevTotalTarget, this.hasCprPrevTotalTarget, this._formatAmount(prevTotal))
    setFooter(this.cprThisTotalTarget, this.hasCprThisTotalTarget, this._formatAmount(thisTotal))
    setFooter(this.cprCompletedTotalTarget, this.hasCprCompletedTotalTarget, this._formatAmount(completedTotal))
    if (this.hasCprPercentTotalTarget) this.cprPercentTotalTarget.textContent = pctTotal
    setFooter(this.cprBalanceTotalTarget, this.hasCprBalanceTotalTarget, this._formatAmount(balanceTotal))
    if (this.hasCprBalanceTotalTarget) {
      const bSpan = this.cprBalanceTotalTarget.querySelector("span:last-child")
      const bSym = this.cprBalanceTotalTarget.querySelector("span:first-child")
      if (bSpan) bSpan.classList.toggle("text-red-600", balanceTotal < 0)
      if (bSym) { bSym.classList.toggle("text-red-600", balanceTotal < 0); bSym.classList.toggle("text-gray-400", balanceTotal >= 0) }
    }

    // Misc expenses totals
    const miscThis = (this.hasCprOtherTarget ? this._parseCurrency(this.cprOtherTarget.value) : 0)
      + (this.hasCprPrintingTarget ? this._parseCurrency(this.cprPrintingTarget.value) : 0)
      + (this.hasCprReimbTarget ? this._parseCurrency(this.cprReimbTarget.value) : 0)
    if (this.hasMiscThisTotalTarget) this.miscThisTotalTarget.textContent = this._formatAmount(miscThis)
    if (this.hasMiscPrevTotalTarget) this.miscPrevTotalTarget.textContent = "0.00"

    // Taxes totals
    const taxesThis = (this.hasCprTaxesTarget ? this._parseCurrency(this.cprTaxesTarget.value) : 0)
      + (this.hasCprTaxes2Target ? this._parseCurrency(this.cprTaxes2Target.value) : 0)
    if (this.hasTaxesThisTotalTarget) this.taxesThisTotalTarget.textContent = this._formatAmount(taxesThis)
    if (this.hasTaxesPrevTotalTarget) this.taxesPrevTotalTarget.textContent = "0.00"
  }

  hideNewCprModal() {
    this._hideRenumberSelect()
    this.newCprModalTarget.classList.add("hidden")
    this._attachmentIds = []
    this._pendingUploads = 0
    if (this.hasCprFileListTarget) this.cprFileListTarget.innerHTML = ""
    showIntercom()
  }

  stopPropagation(e) {
    e.stopPropagation()
  }

  // ── Renumber past PR (dropdown in the edit-past modal header) ──────

  _showRenumberSelect() {
    if (!this.hasRenumberWrapTarget || !this._editSequence) return
    const current = String(this._editSequence)
    const past = Array.from(this.element.querySelectorAll('.payment-request[data-is-past="true"]'))
      .map(r => r.dataset.sequence).filter(Boolean)
    if (!past.includes(current)) past.push(current)
    past.sort((a, b) => a - b)
    this.renumberSelectTarget.innerHTML = past
      .map(s => `<option value="${s}" ${s === current ? "selected" : ""}>#${s}</option>`).join("")
    this.renumberWrapTarget.classList.remove("hidden")
    this.renumberWrapTarget.classList.add("inline-flex")
  }

  _hideRenumberSelect() {
    if (!this.hasRenumberWrapTarget) return
    this.renumberWrapTarget.classList.add("hidden")
    this.renumberWrapTarget.classList.remove("inline-flex")
  }

  // After a past-PR edit saves, apply a number change (swap with the past
  // PR holding the chosen number). Reloads on success — everything renames.
  async _maybeSwapSequence() {
    if (!this.hasRenumberWrapTarget || this.renumberWrapTarget.classList.contains("hidden")) return false
    const target = this.renumberSelectTarget.value
    if (!target || String(target) === String(this._editSequence)) return false
    const resp = await this._ajax("POST", "swap_past_consultant_payment_request_sequence", {
      sequence: this._editSequence,
      target_sequence: target
    })
    if (!resp) return false
    sessionStorage.setItem("prFlashMessage", resp.message || "Updated successfully")
    window.location.reload()
    return true
  }

  // ── File upload ─────────────────────────────────────────────────

  dragoverDropzone(event) {
    event.preventDefault()
    this.cprDropzoneTarget.classList.add("border-brand", "bg-blue-50/50")
  }

  dragleaveDropzone() {
    this.cprDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
  }

  dropDropzone(event) {
    event.preventDefault()
    this.cprDropzoneTarget.classList.remove("border-brand", "bg-blue-50/50")
    const files = Array.from(event.dataTransfer.files).filter(prUploadable)
    if (files.length > 0) this._doFileUpload(files)
  }

  clickDropzone() {
    this.cprFileInputTarget.click()
  }

  selectFiles(event) {
    const files = Array.from(event.target.files).filter(prUploadable)
    if (files.length > 0) this._doFileUpload(files)
    event.target.value = ""
  }

  // ── Pick from Photos ────────────────────────────────────────────
  // photo_picker_controller hands back the ticked photos; the server copies
  // each into a loose attachment so it rides this pay request like an
  // uploaded file (FinancesController#attach_project_photos).

  openPhotoPicker() {
    if (!this.hasPhotoPickerTarget) return
    this.application.getControllerForElementAndIdentifier(this.photoPickerTarget, "photo-picker")?.open()
  }

  async attachPickedPhotos(event) {
    const photos = event.detail?.photos || []
    if (photos.length === 0) return
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = photos.map(p => {
      const chip = this._createFileChip(p.filename || `Photo #${p.id}`, { uploading: true })
      this.cprFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const res = await fetch(`/projects/${this.projectIdValue}/finances/attach_project_photos`, {
        method: "POST",
        headers: { "X-CSRF-Token": this._csrfToken, "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({ photo_ids: photos.map(p => p.id) })
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      const attached = await res.json()
      placeholders.forEach(p => p.remove())
      attached.forEach(a => this._addAttachmentChip(a))
    } catch (e) {
      console.error("Adding photos failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast(this.photoPickerTarget.dataset.errorText || "Couldn't add the photos", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  async _doFileUpload(files) {
    const token = this._csrfToken
    this._pendingUploads++
    this._updateUploadState()

    const placeholders = files.map(file => {
      const chip = this._createFileChip(file.name, { uploading: true })
      this.cprFileListTarget.appendChild(chip)
      return chip
    })

    try {
      const presignUrl = `/projects/${this.projectIdValue}/finances/presign_uploads`
      const presignRes = await fetch(presignUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: files.map(f => ({
            filename: f.name,
            content_type: prContentType(f),
            byte_size: f.size
          }))
        })
      })

      if (!presignRes.ok) {
        const err = await presignRes.json().catch(() => ({}))
        throw new Error(err.error || "Failed to get upload URLs")
      }

      const { uploads } = await presignRes.json()

      const results = await Promise.allSettled(
        uploads.map(async (upload, i) => {
          const res = await fetch(upload.upload_url, {
            method: "PUT",
            headers: { "Content-Type": prContentType(files[i]) },
            body: files[i]
          })
          if (!res.ok) throw new Error(`S3 upload failed: ${res.status}`)
          return upload
        })
      )

      const successful = results.filter(r => r.status === "fulfilled").map(r => r.value)
      if (successful.length === 0) throw new Error("All uploads failed")

      const registerUrl = `/projects/${this.projectIdValue}/finances/register_uploads`
      const registerRes = await fetch(registerUrl, {
        method: "POST",
        headers: { "X-CSRF-Token": token, "Content-Type": "application/json" },
        body: JSON.stringify({
          files: successful.map(u => ({ cache_name: u.cache_name, filename: u.filename }))
        })
      })

      const registered = await registerRes.json()

      placeholders.forEach(p => p.remove())
      registered.forEach(a => this._addAttachmentChip(a))
    } catch (e) {
      console.error("File upload failed:", e)
      placeholders.forEach(p => p.remove())
      this._showToast("File upload failed", "alert")
    } finally {
      this._pendingUploads--
      this._updateUploadState()
    }
  }

  // A saved file on this pay request: its id goes out with the save, its chip
  // can be taken off again.
  _addAttachmentChip(a) {
    const name = a.filename || `File #${a.id}`
    this._attachmentIds.push(a.id)
    const chip = this._createFileChip(name, {
      removable: true,
      onRemove: () => {
        chip.remove()
        this._attachmentIds = this._attachmentIds.filter(id => id !== a.id)
      }
    })
    this.cprFileListTarget.appendChild(chip)
  }

  _updateUploadState() {
    const uploading = this._pendingUploads > 0
    if (this.hasSaveDraftBtnTarget) this._setButtonUploading(this.saveDraftBtnTarget, uploading)
    if (this.hasSignRequestBtnTarget) this._setButtonUploading(this.signRequestBtnTarget, uploading)
    if (this.hasAddPastCprBtnTarget) this._setButtonUploading(this.addPastCprBtnTarget, uploading)
  }

  _setButtonUploading(btn, uploading) {
    btn.disabled = uploading
    btn.classList.toggle("opacity-60", uploading)
    btn.classList.toggle("cursor-not-allowed", uploading)
    btn.classList.toggle("pointer-events-none", uploading)
    if (uploading) {
      if (!btn.dataset.originalText) {
        btn.dataset.originalText = btn.textContent
        btn.style.minWidth = `${btn.offsetWidth}px`
      }
      btn.innerHTML = `<svg class="animate-spin h-4 w-4 inline-block align-middle mr-1 -mt-px" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path></svg>Uploading\u2026`
    } else if (btn.dataset.originalText) {
      btn.textContent = btn.dataset.originalText
      delete btn.dataset.originalText
      btn.style.minWidth = ""
    }
  }

  _createFileChip(name, { removable = false, uploading = false, onRemove = null, url = null } = {}) {
    const chip = document.createElement(url ? "a" : "div")
    if (url) {
      chip.href = url
      chip.target = "_blank"
      chip.rel = "noopener"
    }
    chip.className = `inline-flex items-center gap-1.5 pl-2 ${removable ? "pr-1" : "pr-2"} py-1 rounded-md text-xs${url ? " cursor-pointer hover:opacity-80" : ""}`
    chip.classList.add(uploading ? "chip-uploading" : "chip-file")

    const icon = uploading
      ? `<svg class="w-3.5 h-3.5 shrink-0 animate-spin" fill="none" viewBox="0 0 24 24"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path></svg>`
      : `<svg class="w-3.5 h-3.5 shrink-0 chip-file-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5"><path stroke-linecap="round" stroke-linejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m2.25 0H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z"/></svg>`

    const truncatedName = name.length > 28 ? name.slice(0, 24) + "..." + name.slice(name.lastIndexOf(".")) : name

    let removeBtn = ""
    if (removable) {
      removeBtn = `<button type="button" class="ml-0.5 p-0.5 rounded hover:bg-gray-200 text-gray-400 hover:text-gray-600 transition-colors" data-remove-chip>
        <svg class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2"><path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/></svg>
      </button>`
    }

    chip.innerHTML = `${icon}<span class="truncate max-w-[180px]" title="${this._escAttr(name)}">${truncatedName}</span>${removeBtn}`

    if (removable && onRemove) {
      const btn = chip.querySelector("[data-remove-chip]")
      btn.addEventListener("click", (e) => {
        e.stopPropagation()
        onRemove()
      })
    }

    return chip
  }

  // ── History Log ──────────────────────────────────────────────────

  _renderHistoryLog(entries) {
    if (!this.hasCprHistoryLogTarget) return
    this.cprHistoryLogEntriesTarget.innerHTML = ""
    if (entries.length === 0) {
      this.cprHistoryLogTarget.classList.add("hidden")
      return
    }
    entries.forEach(entry => {
      const p = document.createElement("p")
      p.className = "text-[12px] text-gray-400 leading-5"
      p.textContent = `${entry.datetime} - ${entry.username} (${entry.company_role}) ${entry.message}`
      this.cprHistoryLogEntriesTarget.appendChild(p)
    })
    this.cprHistoryLogTarget.classList.remove("hidden")
  }

  // ── Modal Header Badges ─────────────────────────────────────────

  _renderModalHeaderBadges(data) {
    const isEditing = !!this._editSequence
    const signedCount = data.signed_count || 0
    const tooltip = data.signature_tooltip || ""
    const showRequestBtn = data.show_request_approval_btn || false
    const isPast = data.is_past_payment || false
    const myRoleSigned = isEditing && data.my_role_signed

    // Request Approval button
    if (this.hasModalRequestApprovalBtnTarget) {
      if (isEditing && showRequestBtn && signedCount > 0) {
        this._pendingSignSequence = this._editSequence
        this.modalRequestApprovalBtnTarget.classList.remove("hidden")
        this.modalRequestApprovalBtnTarget.classList.add("inline-flex")
      } else {
        this.modalRequestApprovalBtnTarget.classList.add("hidden")
        this.modalRequestApprovalBtnTarget.classList.remove("inline-flex")
      }
    }

    // Signed badge
    if (this.hasModalSignedBadgeTarget) {
      if (isEditing && (signedCount > 0 || isPast)) {
        this.modalSignedTextTarget.textContent = isPast ? "Past Invoice" : `${signedCount}/2 Signed`
        this.modalSignedCheckTarget.classList.add("hidden")
        if (!isPast && tooltip) {
          this.modalSignedTooltipTarget.setAttribute("data-tip", tooltip)
          this.modalSignedTooltipTarget.setAttribute("data-tip-align", "left")
          this.modalSignedTooltipTarget.setAttribute("data-tip-pos", "below")
          this.modalSignedTooltipTarget.classList.remove("hidden")
        } else {
          this.modalSignedTooltipTarget.classList.add("hidden")
        }
        if (!isPast && myRoleSigned) this.modalSignedCheckTarget.classList.remove("hidden")
        const badge = this.modalSignedBadgeTarget
        badge.classList.remove("bg-green-100", "text-green-600", "bg-amber-100", "text-amber-600", "bg-gray-200", "text-gray-600")
        if (isPast) {
          badge.classList.add("bg-gray-200", "text-gray-600")
        } else if (signedCount < 2) {
          badge.classList.add("bg-amber-100", "text-amber-600")
        } else {
          badge.classList.add("bg-green-100", "text-green-600")
        }
        badge.classList.remove("hidden")
        badge.classList.add("inline-flex")
      } else {
        this.modalSignedBadgeTarget.classList.add("hidden")
        this.modalSignedBadgeTarget.classList.remove("inline-flex")
      }
    }

    // Owner-side approver: the owner, or a stakeholder granted pay-request
    // approval via PM Permissions.
    const ownerApprover = this.isOwnerValue || this.canApproveValue

    // "Pending Consultant Signature" label — owners only, editing draft with 0 signatures
    if (this.hasModalPendingLabelTarget) {
      this.modalPendingLabelTarget.classList.toggle("hidden", !(ownerApprover && isEditing && signedCount === 0 && !isPast))
    }

    // Delete button — consultants editing unsigned drafts, OR past payments
    if (this.hasModalDeleteBtnTarget) {
      const showDelete = isEditing && ((this.isConsultantValue && signedCount === 0 && !isPast) || isPast)
      this.modalDeleteBtnTarget.classList.toggle("hidden", !showDelete)
    }

    // Edit Past PR button — past payments only
    if (this.hasModalEditPastBtnTarget) {
      this.modalEditPastBtnTarget.classList.toggle("hidden", !isPast)
    }

    // Approve — owners only, when consultant has signed and owner hasn't (1/2)
    const showApproveReject = ownerApprover && isEditing && signedCount >= 1 && !myRoleSigned && !isPast
    if (this.hasModalApproveBtnTarget) this.modalApproveBtnTarget.classList.toggle("hidden", !showApproveReject)

    // Reject — owners: show at 1/2 (pending) AND at 2/2 (fully signed, to revoke)
    const showReject = ownerApprover && isEditing && signedCount >= 1 && !isPast
    if (this.hasModalRejectBtnTarget) this.modalRejectBtnTarget.classList.toggle("hidden", !showReject)

    // Sign — consultants only, not signed, not past
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.classList.toggle("hidden", myRoleSigned || ownerApprover || isPast)
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.classList.toggle("hidden", myRoleSigned || ownerApprover || isPast)

    // Unsign — consultant only, when they signed but owner hasn't yet (1/2 only)
    const showUnsign = this.isConsultantValue && myRoleSigned && signedCount < 2
    if (this.hasUnsignBtnTarget) this.unsignBtnTarget.classList.toggle("hidden", !showUnsign)

    // Lock fields when signed
    const locked = myRoleSigned || isPast || (ownerApprover && isEditing)
    this._setModalReadonly(locked)
  }

  _setModalReadonly(locked) {
    // Header fields — white bg when locked, yellow when editable
    const headerInputs = [
      this.hasCprFedIdTarget && this.cprFedIdTarget,
      this.hasCprInvoiceDateTarget && this.cprInvoiceDateTarget,
      this.hasCprInvoiceNumberTarget && this.cprInvoiceNumberTarget,
      this.hasCprPoTarget && this.cprPoTarget,
      this.hasCprVendorIdTarget && this.cprVendorIdTarget,
      this.hasCprProjectNumberTarget && this.cprProjectNumberTarget,
    ].filter(Boolean)
    headerInputs.forEach(input => {
      input.readOnly = locked
      // Handle flatpickr datepicker specially
      const fp = input._flatpickr
      if (fp) {
        if (locked) {
          fp.set("clickOpens", false)
          fp.altInput.readOnly = true
          fp.altInput.classList.remove("!bg-yellow-50")
          fp.altInput.classList.add("bg-white")
        } else {
          fp.set("clickOpens", true)
          fp.altInput.readOnly = false
          fp.altInput.classList.remove("bg-white")
          fp.altInput.classList.add("!bg-yellow-50")
        }
      } else {
        if (locked) {
          input.classList.remove("bg-yellow-50", "!bg-yellow-50")
          input.classList.add("bg-white")
        } else {
          input.classList.remove("bg-white")
          input.classList.add("bg-yellow-50")
        }
      }
    })

    // Line item editable fields
    this.cprLineItemsTarget.querySelectorAll("[data-field='this_application']").forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) {
        td.classList.toggle("bg-yellow-50", !locked)
        td.classList.toggle("bg-white", locked)
      }
    })

    // Misc + taxes — remove yellow bg from parent td when locked
    const miscInputs = [
      this.hasCprOtherTarget && this.cprOtherTarget,
      this.hasCprPrintingTarget && this.cprPrintingTarget,
      this.hasCprReimbTarget && this.cprReimbTarget,
      this.hasCprTaxesTarget && this.cprTaxesTarget,
      this.hasCprTaxesTypeTarget && this.cprTaxesTypeTarget,
      this.hasCprTaxes2Target && this.cprTaxes2Target,
      this.hasCprTaxes2TypeTarget && this.cprTaxes2TypeTarget,
    ].filter(Boolean)
    miscInputs.forEach(input => {
      input.readOnly = locked
      const td = input.closest("td")
      if (td) {
        td.classList.toggle("bg-yellow-50", !locked)
        td.classList.toggle("bg-white", locked)
      }
    })

    // Dropzone — hide when locked
    if (this.hasCprDropzoneTarget) this.cprDropzoneTarget.classList.toggle("hidden", locked)
    if (this.hasUploadLabelTarget) this.uploadLabelTarget.classList.toggle("hidden", locked)
    if (this.hasCprPickPhotosTarget) this.cprPickPhotosTarget.classList.toggle("hidden", locked)

    // Hide remove buttons on file chips when locked
    if (this.hasCprFileListTarget) {
      this.cprFileListTarget.querySelectorAll("[data-remove-chip]").forEach(btn => {
        btn.classList.toggle("hidden", locked)
      })
    }
  }

  // ── Validation ───────────────────────────────────────────────────

  _validateCpr() {
    this._clearValidationErrors()

    // Fed ID required
    if (this.hasCprFedIdTarget && !this.cprFedIdTarget.value.trim()) {
      this._markFieldError(this.cprFedIdTarget)
      this._showToast("FED ID / SSN or SIN/GST Number cannot be empty.", "alert")
      return false
    }

    // Project Number required (when field is shown)
    if (this.hasCprProjectNumberTarget && !this.cprProjectNumberTarget.value.trim()) {
      this._markFieldError(this.cprProjectNumberTarget)
      this._showToast("Please fill in the Project Number.", "alert")
      return false
    }

    // Purchase Order required (when field is shown) + min 6 chars
    if (this.hasCprPoTarget) {
      const po = this.cprPoTarget.value.trim()
      if (!po) {
        this._markFieldError(this.cprPoTarget)
        this._showToast("Please fill in the Purchase Order.", "alert")
        return false
      }
      if (po.length < 6) {
        this._markFieldError(this.cprPoTarget)
        this._showToast("The PO number must have at least 6 characters.", "alert")
        return false
      }
    }

    // Invoice Number required (when custom_invoice_number is enabled)
    if (this.hasCprInvoiceNumberTarget && !this.cprInvoiceNumberTarget.value.trim()) {
      this._markFieldError(this.cprInvoiceNumberTarget)
      this._showToast("Please enter an invoice number.", "alert")
      return false
    }

    // Invoice date required
    if (!this.cprInvoiceDateTarget.value.trim()) {
      this._markFieldError(this.cprInvoiceDateTarget)
      this._showToast("Please enter an invoice date.", "alert")
      return false
    }

    // At least one line item amount (including misc items)
    let hasAmount = false
    this.cprLineItemsTarget.querySelectorAll("[data-field='this_application']").forEach(el => {
      if (this._parseCurrency(el.value) !== 0) hasAmount = true
    })
    if (!hasAmount && this.hasCprOtherTarget && this._parseCurrency(this.cprOtherTarget.value) !== 0) hasAmount = true
    if (!hasAmount && this.hasCprPrintingTarget && this._parseCurrency(this.cprPrintingTarget.value) !== 0) hasAmount = true
    if (!hasAmount && this.hasCprReimbTarget && this._parseCurrency(this.cprReimbTarget.value) !== 0) hasAmount = true
    if (!hasAmount) {
      this._showToast("Please enter at least one line item amount.", "alert")
      return false
    }

    // No line item can exceed its available balance (negative balance)
    let hasOverage = false
    this.cprLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const adjContract = this._parseCurrency(row.querySelector("[data-field='adj-contract']")?.textContent)
      const prevCpr = parseFloat(row.querySelector("[data-field='prev-cpr']")?.dataset.prevTotal) || 0
      const thisApp = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      if (adjContract > 0 && Math.round((prevCpr + thisApp - adjContract) * 100) / 100 > 0) hasOverage = true
    })
    if (hasOverage) {
      this._showToast("One or more line items have an amount above the available balance.", "alert")
      return false
    }

    return true
  }

  _markFieldError(input) {
    input.classList.add("!bg-red-100")
    input.addEventListener("input", () => input.classList.remove("!bg-red-100"), { once: true })
  }

  _clearValidationErrors() {
    this.newCprModalTarget.querySelectorAll(".\\!bg-red-100").forEach(el => {
      el.classList.remove("!bg-red-100")
    })
  }

  _allPreviousPrsSigned() {
    const cards = this.element.querySelectorAll(".payment-request[data-sequence]")
    let currentSeq = parseInt(this._editSequence) || 0
    if (!currentSeq) {
      // New PR — next sequence is max + 1, so all existing cards are "previous"
      cards.forEach(c => { currentSeq = Math.max(currentSeq, parseInt(c.dataset.sequence) || 0) })
      currentSeq += 1
    }
    for (const card of cards) {
      const seq = parseInt(card.dataset.sequence) || 0
      if (seq >= currentSeq) continue
      const isPast = card.dataset.isPast === "true"
      if (isPast) continue
      const signedCount = parseInt(card.dataset.signedCount) || 0
      if (signedCount < 2) {
        this._showToast("All previous pending PRs must be fully signed before proceeding.", "alert")
        return false
      }
    }
    return true
  }

  // ── Save CPR ────────────────────────────────────────────────────

  async saveDraft() { await this._submitCpr(false) }

  async signAndRequest() {
    if (!this._allPreviousPrsSigned()) return
    if (this.requirePrAttachmentValue && this._attachmentIds.length === 0) {
      this._showToast("Please add at least one PDF or photo.", "alert")
      return
    }
    await this._submitCpr(true)
  }

  async _submitCpr(signAfter) {
    if (this._submitting) return
    if (!this._skipValidation && !this._validateCpr()) return

    const lineItemsArr = this._collectCprLineItems()

    this._submitting = true
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = true
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = true

    const sequence = this._editSequence || await this._getNextCprSequence()
    const isUpdate = !!this._editSequence

    let action
    if (isUpdate) {
      action = "update_consultant_payment_request"
    } else {
      action = this._isPast ? "create_past_payment_consultant_payment_request" : "create_consultant_payment_request"
    }

    const params = {
      sequence: sequence,
      line_items_arr: JSON.stringify(lineItemsArr),
      invoice_date: this.cprInvoiceDateTarget.value,
      invoice_number: this.hasCprInvoiceNumberTarget ? this.cprInvoiceNumberTarget.value : "",
      fedid: this.hasCprFedIdTarget ? this.cprFedIdTarget.value : "",
      po_number: this.hasCprPoTarget ? this.cprPoTarget.value : "",
      project_number: this.hasCprProjectNumberTarget ? this.cprProjectNumberTarget.value : "",
      vendor_id: this.hasCprVendorIdTarget ? this.cprVendorIdTarget.value : "",
      other: this.hasCprOtherTarget ? this._parseCurrency(this.cprOtherTarget.value) || "" : "",
      printing: this.hasCprPrintingTarget ? this._parseCurrency(this.cprPrintingTarget.value) || "" : "",
      reimb: this.hasCprReimbTarget ? this._parseCurrency(this.cprReimbTarget.value) || "" : "",
      taxes: this.hasCprTaxesTarget ? this._parseCurrency(this.cprTaxesTarget.value) || "" : "",
      taxes_type: this.hasCprTaxesTypeTarget ? this.cprTaxesTypeTarget.value : "",
      taxes2: this.hasCprTaxes2Target ? this._parseCurrency(this.cprTaxes2Target.value) || "" : "",
      taxes2_type: this.hasCprTaxes2TypeTarget ? this.cprTaxes2TypeTarget.value : "",
      attachment_ids: this._attachmentIds.join(" ")
    }

    const method = isUpdate ? "PUT" : "POST"
    let saveResp
    try {
      saveResp = await this._ajax(method, action, params)
    } catch (e) {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
      throw e
    }

    if (signAfter && !this._isPast) {
      // Show signature acknowledge modal before signing
      this._pendingSignSequence = sequence
      this._signFromEditModal = true
      this._signFromEditIsNew = !isUpdate
      this._signFromEditSaveResp = saveResp
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
      this.signModalTarget.classList.remove("hidden")
      return
    }

    if (!saveResp) {
      this._submitting = false
      if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
      if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
      return
    }

    // Update page card
    if (isUpdate) {
      if (this._isPast && await this._maybeSwapSequence()) return
      this._updatePageCard(saveResp)
    } else {
      this._insertNewPageCard(saveResp)
    }
    this._updateSummaryTable(saveResp.overall_summary)

    this._submitting = false
    if (this.hasSaveDraftBtnTarget) this.saveDraftBtnTarget.disabled = false
    if (this.hasSignRequestBtnTarget) this.signRequestBtnTarget.disabled = false
    this.hideNewCprModal()
    this._showToast("Saved", "success")
  }

  _collectCprLineItems() {
    const items = []
    this.cprLineItemsTarget.querySelectorAll("tr").forEach(row => {
      const lineItemId = row.dataset.lineItemId
      const amount = this._parseCurrency(row.querySelector("[data-field='this_application']")?.value)
      if (amount !== 0) {
        items.push([lineItemId, amount])
      }
    })
    return items
  }

  async _getNextCprSequence() {
    const prElements = this.element.querySelectorAll(".payment-request[data-sequence]")
    let max = 0
    prElements.forEach(el => {
      const seq = parseInt(el.dataset.sequence)
      if (seq > max) max = seq
    })
    return max + 1
  }

  // ── Add Past Payment Request ────────────────────────────────────

  async addPastPaymentRequest() {
    if (!this._allPreviousPrsSigned()) return
    if (!this._validateCpr()) return

    const lineItemsArr = this._collectCprLineItems()
    if (lineItemsArr.length === 0) {
      this._showToast("At least one line item with amount is required.", "alert")
      return
    }

    if (this.requirePrAttachmentValue && this._attachmentIds.length === 0) {
      this._showToast("Please add at least one PDF or photo.", "alert")
      return
    }

    // Show confirmation modal
    const nextSequence = await this._getNextCprSequence()
    if (this.hasPastCprSequenceLabelTarget) {
      this.pastCprSequenceLabelTarget.textContent = `PR#${nextSequence}`
    }
    this.pastCprConfirmModalTarget.classList.remove("hidden")
  }

  async confirmPastCpr() {
    this.pastCprConfirmModalTarget.classList.add("hidden")
    this._isPast = true
    this._skipValidation = true
    await this._submitCpr(false)
    this._skipValidation = false
  }

  hidePastCprConfirmModal() {
    this.pastCprConfirmModalTarget.classList.add("hidden")
  }

  // ── Delete ──────────────────────────────────────────────────────

  deleteFromModal() {
    const cards = this.element.querySelectorAll(".payment-request[data-sequence]")
    let maxSeq = 0
    cards.forEach(el => { const s = parseInt(el.dataset.sequence); if (s > maxSeq) maxSeq = s })
    if (parseInt(this._editSequence) !== maxSeq) {
      this._showToast("Must delete the most recent item first. To delete this item, please first delete any more recent records.", "alert")
      return
    }
    this._pendingDeleteSequence = this._editSequence
    this.deleteModalTarget.classList.remove("hidden")
  }

  hideDeleteModal() { this.deleteModalTarget.classList.add("hidden") }

  async confirmDelete() {
    const resp = await this._ajax("DELETE", "destroy_cpr_line_items", { sequence: this._pendingDeleteSequence })
    this.hideDeleteModal()
    this.hideNewCprModal()
    this._removePageCard(this._pendingDeleteSequence)

    if (resp && resp.has_any_cprs) {
      this._updateSummaryTable(resp.overall_summary)
    } else {
      if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.add("hidden")
      if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.remove("hidden")
    }
    this._showToast("Deleted", "success")
  }

  // ── Edit Past CPR ──────────────────────────────────────────────

  editPastCpr() {
    this._setModalReadonly(false)
    this._showRenumberSelect()
    if (this.hasModalEditPastBtnTarget) this.modalEditPastBtnTarget.classList.add("hidden")
    if (this.hasSaveDraftBtnTarget) {
      this.saveDraftBtnTarget.classList.remove("hidden")
      this.saveDraftBtnTarget.textContent = "Update Past PR"
    }
  }

  // ── Sign / Unsign ──────────────────────────────────────────────

  showSignModal(sequence) {
    this._pendingSignSequence = sequence
    this.signModalTarget.classList.remove("hidden")
  }

  hideSignModal() {
    if (this._signFromEditModal || this._approveAfterAcknowledge) {
      this.signModalTarget.classList.add("hidden")
      this._signFromEditModal = false
      this._signFromEditIsNew = false
      this._signFromEditSaveResp = null
      this._approveAfterAcknowledge = false
      return
    }
    this.signModalTarget.classList.add("hidden")
  }

  async confirmSign(event) {
    // One request per gesture — key-repeat (held Enter) or double-clicks fire
    // this repeatedly while the request is still in flight.
    if (this._confirmInFlight) return
    this._confirmInFlight = true
    const confirmBtn = event?.currentTarget
    if (confirmBtn) confirmBtn.disabled = true
    try {
      await this._confirmSignInner()
    } finally {
      this._confirmInFlight = false
      if (confirmBtn) confirmBtn.disabled = false
    }
  }

  async _confirmSignInner() {
    const fromEditModal = this._signFromEditModal
    const isNew = this._signFromEditIsNew
    const fromApprove = this._approveAfterAcknowledge
    this._signFromEditModal = false
    this._signFromEditIsNew = false
    this._signFromEditSaveResp = null
    this._approveAfterAcknowledge = false

    let resp = await this._ajax("POST", "sign_cpr", { sequence: this._pendingSignSequence })
    this.signModalTarget.classList.add("hidden")

    // A failed sign returns null; _ajax already surfaced the reason, so bail.
    if (!resp) return

    // Phase 6 — soft insurance gate: resolve a non-compliant-payee decision
    // (owner: approve-anyway/reject; consultant: heads-up) then continue.
    if (resp.requires_compliance_decision) {
      resp = await this._resolveComplianceDecision(resp, {
        signAction: "sign_cpr", signParams: { sequence: this._pendingSignSequence },
        rejectAction: "reject_cpr", rejectParams: { sequence: this._pendingSignSequence }
      })
      if (!resp) return
    }

    if (fromApprove) {
      if (resp) {
        this._updatePageCard(resp)
        this._updateModalState(resp)
        this._updateSummaryTable(resp.overall_summary)
      }
      this.hideNewCprModal()
      this._showToast("Approved", "success")
      return
    }

    if (fromEditModal) {
      if (resp) {
        if (isNew) {
          this._insertNewPageCard(resp)
        } else {
          this._updatePageCard(resp)
        }
        this._updateSummaryTable(resp.overall_summary)
        this._updateModalState(resp)
      }

      // Show request approval modal for consultants
      if (this.isConsultantValue) {
        this._signedBeforeRequest = true
        this.requestApprovalModalTarget.classList.remove("hidden")
      } else {
        this.hideNewCprModal()
      }
      this._showToast("Signed", "success")
      return
    }

    // Standalone sign from page card
    if (resp) {
      this._updatePageCard(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this._showToast("Signed", "success")
  }

  unsignFromModal() {
    this._pendingSignSequence = this._editSequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  showUnsignModal(sequence) {
    this._pendingSignSequence = sequence
    this.unsignModalTarget.classList.remove("hidden")
  }

  hideUnsignModal() { this.unsignModalTarget.classList.add("hidden") }

  async confirmUnsign() {
    const resp = await this._ajax("POST", "unsign_cpr", { sequence: this._pendingSignSequence })
    this.hideUnsignModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this._showToast("Signature removed", "success")
  }

  // ── Approve / Reject ────────────────────────────────────────────

  approveCpr() {
    this._pendingSignSequence = this._editSequence
    this._approveAfterAcknowledge = true
    this.signModalTarget.classList.remove("hidden")
  }

  showRejectCprModal() { this.rejectCprModalTarget.classList.remove("hidden") }
  hideRejectCprModal() {
    this.rejectCprModalTarget.classList.add("hidden")
    if (this.hasRejectCommentTarget) this.rejectCommentTarget.value = ""
  }

  async confirmRejectCpr() {
    const comment = this.hasRejectCommentTarget ? this.rejectCommentTarget.value : ""
    const resp = await this._ajax("POST", "reject_cpr", { sequence: this._editSequence, comment })
    this.hideRejectCprModal()
    if (resp) {
      this._updatePageCard(resp)
      this._updateModalState(resp)
      this._updateSummaryTable(resp.overall_summary)
    }
    this.hideNewCprModal()
    this._showToast("Rejected", "success")
  }

  // ── Request approval ───────────────────────────────────────────

  showRequestApprovalModal() { this.requestApprovalModalTarget.classList.remove("hidden") }
  hideRequestApprovalModal() {
    this.requestApprovalModalTarget.classList.add("hidden")
    if (this._signedBeforeRequest) {
      this._signedBeforeRequest = false
      this.hideNewCprModal()
    }
  }

  async sendApprovalRequest() {
    const sections = this.requestApprovalModalTarget.querySelectorAll("[data-company-section]")
    for (const section of sections) {
      if (section.classList.contains("hidden")) continue
      const checked = section.querySelectorAll("input[name='approval_email']:checked")
      if (checked.length === 0) {
        this._showToast("Please select at least one person from each company.", "alert")
        return
      }
    }

    const checkboxes = this.requestApprovalModalTarget.querySelectorAll("input[name='approval_email']:checked")
    const emails = Array.from(checkboxes).map(cb => cb.dataset.email)
    if (emails.length === 0) return

    const resp = await this._ajax("POST", "request_cpr_approval", {
      sequence: this._pendingSignSequence || this._editSequence,
      emails: JSON.stringify(emails)
    })
    if (resp) this._updatePageCard(resp)
    this._signedBeforeRequest = false
    this.requestApprovalModalTarget.classList.add("hidden")
    this.hideNewCprModal()
    this._showToast("Approval request sent", "success")
  }

  // ── General Info ───────────────────────────────────────────────

  showGeneralInfo() { this.generalInfoModalTarget.classList.remove("hidden") }
  hideGeneralInfo() { this.generalInfoModalTarget.classList.add("hidden") }

  async saveGeneralInfo() {
    const params = {
      building_type: this.hasGiBuildingTypeTarget ? this.giBuildingTypeTarget.value : "",
      stake_mission: this.hasGiStakeMissionTarget ? this.giStakeMissionTarget.value : "",
      project_number: this.hasGiProjectNumberTarget ? this.giProjectNumberTarget.value : "",
      po_number: this.hasGiPoNumberTarget ? this.giPoNumberTarget.value : "",
      vendor_id: this.hasGiVendorIdTarget ? this.giVendorIdTarget.value : "",
      fedid: this.hasGiFedIdTarget ? this.giFedIdTarget.value : "",
      start_date: this.hasGiStartDateTarget ? this.giStartDateTarget.value : "",
      end_date: this.hasGiEndDateTarget ? this.giEndDateTarget.value : ""
    }

    if (this.hasGiConsultantCompanyIdTarget) {
      params.consultant_company_id = this.giConsultantCompanyIdTarget.value
      params.consultant_address = this.hasGiConsultantAddressTarget ? this.giConsultantAddressTarget.value : ""
      params.consultant_city = this.hasGiConsultantCityTarget ? this.giConsultantCityTarget.value : ""
      params.consultant_state = this.hasGiConsultantStateTarget ? this.giConsultantStateTarget.value : ""
      params.consultant_zip = this.hasGiConsultantZipTarget ? this.giConsultantZipTarget.value : ""
    }

    await this._ajax("PUT", "save_cpr_general_info", params)
    this.hideGeneralInfo()
    this._showToast("General info saved", "success")
  }

  // ── Page card DOM helpers ──────────────────────────────────────

  _updateSummaryTable(summary) {
    if (!summary) return
    if (this.hasSummaryScheduledTarget) this.summaryScheduledTarget.innerHTML = `<b>${this._formatAmount(summary.scheduled_total)}</b>`
    if (this.hasSummaryFaTarget) this.summaryFaTarget.innerHTML = `<b>${this._formatAmount(summary.fee_adjustment_total)}</b>`
    if (this.hasSummaryAcaTarget) this.summaryAcaTarget.innerHTML = `<b>${this._formatAmount(summary.aca)}</b>`
    if (this.hasSummaryCompletedTarget) this.summaryCompletedTarget.innerHTML = `<b>${this._formatAmount(summary.complete_to_date)}</b>`
    if (this.hasSummaryPercentTarget) this.summaryPercentTarget.textContent = summary.percent_completed
    if (this.hasSummaryBalanceTarget) this.summaryBalanceTarget.innerHTML = `<b>${this._formatAmount(summary.balance)}</b>`
    if (this.hasOverallTotalsTarget) this.overallTotalsTarget.classList.remove("hidden")
  }

  _buildBadgeHTML(data) {
    const signedCount = data.signed_count || 0
    const isPast = data.is_past_payment || false
    const tooltip = data.signature_tooltip || ""
    const myRoleSigned = data.my_role_signed || false

    if (isPast) {
      return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">Past Invoice</span>`
    }

    if (signedCount > 0) {
      const badgeBg = signedCount < 2 ? "bg-amber-100 text-amber-600" : "bg-green-100 text-green-600"
      let iconHtml = (signedCount >= 2 && myRoleSigned) ? SVG_CHECK : ""
      let tooltipHtml = tooltip ? `<span class="shrink-0 cursor-help flex items-center" data-tip="${this._escAttr(tooltip)}" data-tip-align="left" data-tip-pos="below">${SVG_INFO_CIRCLE}</span>` : ""
      return `<span class="inline-flex items-center gap-1 ${badgeBg} text-[12px] font-medium rounded-lg px-2.5 py-[2px]">${iconHtml} ${signedCount}/2 Signed ${tooltipHtml}</span>`
    }

    return `<span class="inline-flex items-center gap-1 bg-gray-200 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px]">DRAFT</span>`
  }

  _updatePageCard(data) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${data.sequence}"]`)
    if (!card) return

    card.dataset.signedCount = data.signed_count || 0
    card.dataset.isPast = data.is_past_payment || false

    // Badge
    const badgeArea = card.querySelector("[data-badge-area]")
    if (badgeArea) badgeArea.innerHTML = this._buildBadgeHTML(data)

    // Last updated
    const lastUpdated = card.querySelector("[data-last-updated]")
    if (lastUpdated && data.last_updated_at) {
      lastUpdated.textContent = `Last updated on ${data.last_updated_at} by ${data.who_updated || ""}`
    }

    // Total amount
    const total = (parseFloat(data.cpr_total) || 0) + (parseFloat(data.misc_total) || 0)
    const totalEl = card.querySelector("[data-card-total-amount]")
    if (totalEl) {
      totalEl.textContent = `${this.currencyValue}${this._formatAmount(total)}`
    }
  }

  _buildFullCardHTML(data) {
    const seq = data.sequence
    const isPast = data.is_past_payment || false
    const pdfUrl = `/projects/${this.projectIdValue}/finances/cpr_export_with_pdfs/${seq}.pdf`
    const total = (parseFloat(data.cpr_total) || 0) + (parseFloat(data.misc_total) || 0)
    const cur = this.currencyValue

    return `<div class="payment-request flex items-center justify-between px-3 py-2.5 text-[13px] text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors cursor-pointer" data-sequence="${seq}" data-signed-count="${data.signed_count || 0}" data-is-past="${isPast}" data-action="click->consultant-pay-request#editPaymentRequest">
      <div class="flex items-center gap-3">
        <span class="font-semibold text-gray-700 text-[14px]">Payment Request #${seq}</span>
        <span data-badge-area>${this._buildBadgeHTML(data)}</span>
      </div>
      <div class="flex items-center gap-3">
        <div class="relative" data-controller="dropdown">
          <button data-action="click->dropdown#toggle" class="inline-flex items-center gap-1 bg-gray-200 hover:bg-gray-300 text-gray-600 text-[12px] font-medium rounded-lg px-2.5 py-[2px] transition-colors cursor-pointer">Export <svg class="w-2.5 h-2.5" fill="currentColor" viewBox="0 0 10 6"><path d="M0 0l5 6 5-6z"/></svg></button>
          <div data-dropdown-target="menu" class="hidden absolute right-0 mt-1 bg-white border border-gray-300 rounded-lg shadow-lg z-30 min-w-[100px] py-1">
            <a href="${pdfUrl}" target="_blank" data-action="click->consultant-pay-request#stopPropagation" class="block px-3 py-1 text-[13px] text-gray-700 hover:bg-gray-50">PDF</a>
          </div>
        </div>
        <span data-last-updated class="text-gray-400 text-[12px] italic">Last updated on ${data.last_updated_at || ""} by ${data.who_updated || ""}</span>
        <span data-card-total-amount class="font-semibold text-gray-700 text-[14px]">${cur}${this._formatAmount(total)}</span>
      </div>
    </div>`
  }

  _updateBreadcrumbCount(delta) {
    const badge = document.getElementById("cpr-breadcrumb-count")
    if (badge) {
      const current = parseInt(badge.textContent) || 0
      badge.textContent = Math.max(0, current + delta)
    }
  }

  _insertNewPageCard(data) {
    if (this.hasEmptyStateTarget) this.emptyStateTarget.classList.add("hidden")
    this._updateBreadcrumbCount(1)

    const cardsContainer = this.hasPrCardsTarget ? this.prCardsTarget : null
    if (!cardsContainer) return

    // Add container styling if first PR
    if (!cardsContainer.querySelector(".payment-request")) {
      cardsContainer.classList.add("flex", "flex-col", "gap-2")
    }

    const newCardHTML = this._buildFullCardHTML(data)
    const newSeq = data.sequence

    // Insert in descending sequence order
    const existingCards = cardsContainer.querySelectorAll(".payment-request[data-sequence]")
    let inserted = false
    for (const card of existingCards) {
      const cardSeq = parseInt(card.dataset.sequence)
      if (newSeq > cardSeq) {
        card.insertAdjacentHTML("beforebegin", newCardHTML)
        inserted = true
        break
      }
    }
    if (!inserted) {
      cardsContainer.insertAdjacentHTML("beforeend", newCardHTML)
    }
  }

  _removePageCard(sequence) {
    const card = this.element.querySelector(`.payment-request[data-sequence="${sequence}"]`)
    if (card) {
      card.remove()
      this._updateBreadcrumbCount(-1)
    }
  }

  _updateModalState(data) {
    this._renderModalHeaderBadges(data)
    this._renderHistoryLog(data.history_log || [])
  }

  // ── Toast ──────────────────────────────────────────────────────

  _showToast(message, type = "success") {
    const cssType = (type === "error" || type === "alert") ? "alert" : "notice"
    const icon = cssType === "notice"
      ? '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z"/></svg>'
      : '<svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-5 h-5"><path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m9-.75a9 9 0 1 1-18 0 9 9 0 0 1 18 0Zm-9 3.75h.008v.008H12v-.008Z"/></svg>'
    const el = document.createElement("div")
    el.className = `flash-toast-${cssType}`
    el.style.zIndex = "9999"
    el.setAttribute("data-controller", "flash")
    el.setAttribute("data-flash-dismiss-after-value", "5000")
    el.setAttribute("role", "alert")
    const iconDiv = document.createElement("div")
    iconDiv.className = "shrink-0"
    iconDiv.innerHTML = icon
    const msgP = document.createElement("p")
    msgP.className = "flex-1 text-sm"
    msgP.textContent = message
    el.appendChild(iconDiv)
    el.appendChild(msgP)
    document.body.prepend(el)
  }

  // ── Phase 6: soft insurance gate decision ──────────────────────
  // Owner (final approver) chooses approve-anyway or reject; any other
  // signer gets a heads-up. Returns the real sign response when the user
  // proceeds, or null when they cancel/reject.
  async _resolveComplianceDecision(decision, { signAction, signParams, rejectAction, rejectParams }) {
    if (decision.is_owner) {
      const proceed = await confirmAction({
        title: "Insurance not compliant",
        message: `${decision.compliance_warning} You can approve this pay request anyway, or reject it.`,
        confirmLabel: "Approve anyway",
        cancelLabel: "Reject",
        danger: true
      })
      if (!proceed) {
        await this._ajax("POST", rejectAction, { ...rejectParams, comment: "Insurance not compliant" })
        this._showToast("Pay request rejected.", "alert")
        window.location.reload()
        return null
      }
    } else {
      const proceed = await confirmAction({
        title: "Insurance not up to date",
        message: `${decision.compliance_warning} Please update your coverage with the project owner. You can continue for now.`,
        confirmLabel: "Continue",
        cancelLabel: "Cancel"
      })
      if (!proceed) return null
    }
    return await this._ajax("POST", signAction, { ...signParams, acknowledge_compliance: true })
  }

  // ── AJAX helper ────────────────────────────────────────────────

  async _ajax(method, action, params = {}) {
    const url = `/projects/${this.projectIdValue}/finances/${action}`
    const body = new FormData()
    Object.entries(params).forEach(([k, v]) => {
      if (v !== "" && v !== null && v !== undefined) body.append(k, v)
    })

    const options = {
      method: method === "GET" ? "GET" : "POST",
      headers: { "X-CSRF-Token": this._csrfToken },
    }

    if (method !== "GET") {
      if (method !== "POST") body.append("_method", method.toLowerCase())
      options.body = body
    }

    try {
      const resp = await fetch(url, options)
      if (!resp.ok) {
        const data = await resp.json().catch(() => ({}))
        this._showToast(data.error || "Request failed. Please try again.", "error")
        return null
      }
      if (resp.headers.get("content-type")?.includes("application/json")) {
        return await resp.json()
      }
      return true
    } catch (e) {
      console.error("CPR request failed:", e)
      this._showToast("Request failed. Please try again.", "error")
      return null
    }
  }
}
